import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";


import api from "../../api";

import styles from "./TaskAssignList.module.css";
import TaskQnA from "./TaskQnA"; 


export default function TaskAssignList() {
  const navigate = useNavigate();
  const [tasks, setTasks]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [assigneeFilter, setAssigneeFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [me, setMe] = useState(null);

  const [unreadTaskIds, setUnreadTaskIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    api.get("/tasks/notifications/").then(res => {
      const ids = new Set(
        res.data
          .filter(n => !n.is_read && ["task_completed","task_comment_to_assigner","task_question"].includes(n.ntype))
          .map(n => n.task)
      );
      setUnreadTaskIds(ids);
    }).catch(() => {});
  }, []);
  useEffect(() => {
    api
      .get("/tasks/assigned/")
      .then((res) => setTasks(res.data))
      .catch(() => setTasks([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {                          // ← 추가
    api.get("/users/me/").then(res => setMe(res.data.id));
  }, []);

  const location = useLocation();
  useEffect(() => {
    if (!location.state?.openTaskId || tasks.length === 0) return;
    const target = tasks.find(t => t.id === location.state.openTaskId);
    if (target) setSelected(target);
  }, [tasks, location.state]);


  // 수신자 목록 추출 (중복 제거)
  const assigneeOptions = [
    ...new Map(tasks.map((t) => [t.assignee, {
      name: t.assignee_name,
      rank: t.assignee_position_rank ?? 99,
    }])).entries(),
  ]
    .sort((a, b) => a[1].rank - b[1].rank)
    .map(([id, info]) => [id, info.name]);

  // 필터 적용
  const filtered = tasks.filter((t) => {
    const statusMatch =
      statusFilter === "all" ? true
      : statusFilter === "chat" ? t.comment_count > 0
      : t.status === statusFilter;
    const assigneeMatch =
      assigneeFilter === "all" || String(t.assignee) === assigneeFilter;
    const q = searchQuery.trim().toLowerCase();
    const searchMatch =
      !q ||
      t.title.toLowerCase().includes(q) ||
      t.content?.toLowerCase().includes(q) ||
      t.assignee_name?.toLowerCase().includes(q);
    return statusMatch && assigneeMatch && searchMatch;
  });

  // 요약 숫자
  const total    = tasks.length;
  const done     = tasks.filter((t) => t.status === "done").length;
  const pending  = tasks.filter((t) => t.status === "pending").length;
  const inprog   = tasks.filter((t) => t.status === "inprogress").length;
  const hasChat  = tasks.filter((t) => t.comment_count > 0 && t.status !== "done").length;


  const starDisplay = (n) =>
    "★".repeat(n) + "☆".repeat(3 - n);

  const statusBadge = (s) => {
    if (s === "pending")    return <span className={styles.bPending}>대기</span>;
    if (s === "inprogress") return <span className={styles.bInprogress}>진행중</span>;
    if (s === "done")       return <span className={styles.bDone}>완료</span>;
    return null;
  };

  const isDueSoon = (due) => {
    if (!due) return false;
    const diff = (new Date(due) - new Date()) / (1000 * 60 * 60 * 24);
    return diff <= 2 && diff >= 0;
  };

  const isOverdue = (due, status) => {
    if (!due || status === "done") return false;
    return new Date(due) < new Date();
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.panel}>

        {/* 상단 */}
        <div className={styles.top}>
          <span className={styles.title}>내가 지시한 업무 목록</span>
          <button
            className={styles.btnNew}
            onClick={() => navigate("/task/assign/new")}
          >
            + 업무 지시 작성
          </button>
        </div>

        {/* 요약 카드 */}
        <div className={styles.summary}>
          <div className={styles.scard}>
            <div className={styles.scardLabel}>전체</div>
            <div className={styles.scardNum}>{total}</div>
          </div>
          <div className={styles.scard}>
            <div className={styles.scardLabel}>대기</div>
            <div className={`${styles.scardNum} ${styles.amber}`}>{pending}</div>
          </div>
          <div className={styles.scard}>
            <div className={styles.scardLabel}>질문/답변 중</div>
            <div className={`${styles.scardNum} ${styles.blue}`}>{hasChat}</div>
          </div>
          <div className={styles.scard}>
            <div className={styles.scardLabel}>진행중</div>
            <div className={`${styles.scardNum} ${styles.amber}`}>{inprog}</div>
          </div>

          <div className={styles.scard}>
            <div className={styles.scardLabel}>완료</div>
            <div className={`${styles.scardNum} ${styles.green}`}>{done}</div>
          </div>
        </div>

        {/* 필터 행 */}
        <div className={styles.filterRow}>
          {/* 상태 필터 */}
          <div className={styles.filterGroup}>
            {[
              { key: "all",        label: "전체" },
              { key: "pending",    label: "대기" },
              { key: "chat",       label: "질문/답변" },  // ← 추가

              { key: "inprogress", label: "진행중" },
              { key: "done",       label: "완료" },
            ].map((f) => (
              <button
                key={f.key}
                className={`${styles.filterBtn} ${statusFilter === f.key ? styles.filterOn : ""}`}
                onClick={() => setStatusFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="제목·내용·수신자 검색"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {/* 수신자 필터 */}
          <select
            className={styles.assigneeSelect}
            value={assigneeFilter}
            onChange={(e) => setAssigneeFilter(e.target.value)}
          >
            <option value="all">수신자 전체</option>
            {assigneeOptions.map(([id, name]) => (
              <option key={id} value={String(id)}>
                {name}
              </option>
            ))}

          </select>
        </div>

        {/* 모바일 카드 리스트 */}
        <div className={styles.mobileList}>
          {loading ? (
            <div className={styles.empty}>불러오는 중...</div>
          ) : filtered.length === 0 ? (
            <div className={styles.empty}>해당하는 업무가 없습니다.</div>
          ) : (
            filtered.map((t) => (
              <div key={t.id} className={styles.taskCard}>
                <div className={styles.taskCardTitle}>{t.title}</div>
                <div className={styles.taskCardRow}>
                  <span className={styles.taskCardLabel}>수신자</span>
                  <span className={styles.taskCardValue}>{t.assignee_name}</span>
                </div>
                <div className={styles.taskCardRow}>
                  <span className={styles.taskCardLabel}>중요도</span>
                  <span className={`${styles.taskCardValue} ${styles.stars}`}>
                    {"★".repeat(t.priority)}{"☆".repeat(3 - t.priority)}
                  </span>
                </div>
                <div className={styles.taskCardRow}>
                  <span className={styles.taskCardLabel}>기한</span>
                  <span className={`${styles.taskCardValue} ${isOverdue(t.due_date, t.status) ? styles.overdue : isDueSoon(t.due_date) ? styles.dueSoon : ""}`}>
                    {t.due_date?.slice(0, 16).replace("T", " ")}
                    {isOverdue(t.due_date, t.status) && <span className={styles.dueTag}>기한초과</span>}
                    {isDueSoon(t.due_date) && !isOverdue(t.due_date, t.status) && <span className={styles.dueTagWarn}>임박</span>}
                  </span>
                </div>
                <div className={styles.taskCardRow}>
                  <span className={styles.taskCardLabel}>지시일</span>
                  <span className={styles.taskCardValue}>{t.created_at?.slice(0, 10)}</span>
                </div>
                <div className={styles.taskCardRow}>
                  <span className={styles.taskCardLabel}>상태</span>
                  <span className={styles.taskCardValue}>
                    {unreadTaskIds.has(t.id) && <span style={{marginRight:4}}>🔔</span>}
                    {statusBadge(t.status)}


                    {t.comment_count > 0 && (
                      <span className={styles.bChat}>💬</span>
                    )}


                  </span>
                </div>
                <div className={styles.taskCardBtns}>
                  <button
                    className={styles.btnDetail}
                    onClick={() => setSelected(t)}
                  >
                    상세보기
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* 테이블 (PC용 — 모바일에서 숨김) */}
        {loading ? (


          <div className={styles.empty}>불러오는 중...</div>
        ) : filtered.length === 0 ? (
          <div className={styles.empty}>해당하는 업무가 없습니다.</div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>

                <tr>
                  <th style={{width:"28%"}}>업무 제목</th>
                  <th style={{width:"10%"}}>수신자</th>
                  <th style={{width:"8%"}}>중요도</th>
                  <th style={{width:"16%"}}>기한</th>
                  <th style={{width:"10%"}}>지시일</th>
                  <th style={{width:"8%"}}>상태</th>
                  <th style={{width:"10%"}}></th>
                </tr>


              </thead>
              <tbody>
                {filtered.map((t) => (
                  <tr key={t.id}>
                    <td className={styles.tdTitle}>{t.title}</td>
                    <td>{t.assignee_name}</td>
                    <td>
                      <span className={styles.stars}>
                        {"★".repeat(t.priority)}
                      </span>
                      <span className={styles.starsGray}>
                        {"★".repeat(3 - t.priority)}
                      </span>
                    </td>
                    <td
                      className={
                        isOverdue(t.due_date, t.status)
                          ? styles.overdue
                          : isDueSoon(t.due_date)
                          ? styles.dueSoon
                          : ""
                      }
                    >
                      {t.due_date?.slice(0, 16).replace("T", " ")}

                      {isOverdue(t.due_date, t.status) && (
                        <span className={styles.dueTag}>기한초과</span>
                      )}
                      {isDueSoon(t.due_date) && !isOverdue(t.due_date, t.status) && (
                        <span className={styles.dueTagWarn}>임박</span>
                      )}
                    </td>
                    <td>{t.created_at?.slice(0, 10)}</td>
                    <td>
                      {unreadTaskIds.has(t.id) && <span style={{marginRight:4}}>🔔</span>}
                      {statusBadge(t.status)}

                      {t.comment_count > 0 && (
                        <span className={styles.bChat}>💬</span>
                      )}


                    </td>

                    <td style={{display:"flex", gap:4, justifyContent:"center", flexWrap:"nowrap"}}>
                      <button
                        className={styles.btnDetail}
                        onClick={() => setSelected(t)}
                      >
                        상세보기
                      </button>

                      <button className={styles.btnAnswer} onClick={() => setSelected(t)}>질문/답변</button>

                    </td>


                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}


        {selected && (
          <div style={{
            position:"fixed", inset:0, background:"rgba(0,0,0,0.45)",
            display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000
          }} onClick={() => setSelected(null)}>
            <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>

              {/* 왼쪽: 상세 내용 */}
              <div className={styles.modalLeft}>
                <span style={{fontSize:15, fontWeight:700, display:"block", marginBottom:12}}>{selected.title}</span>



                <div className={styles.modalGrid}>

                  {[
                    ["지시자", selected.assigner_name],
                    ["수신자", selected.assignee_name],
                    ["중요도", "★".repeat(selected.priority) + "☆".repeat(3 - selected.priority)],
                    ["기한",   selected.due_date?.slice(0,16).replace("T"," ")],
                    ["상태",   selected.status_display],
                    ["지시일", selected.created_at?.slice(0,10)],


                    ["업무종류", selected.work_category_name || "미지정"],
                    ["업무난이도", selected.difficulty ? `${selected.difficulty} / 10` : "미지정"],

                    ["예상 소요", selected.est_minutes != null
                      ? selected.est_minutes < 60
                        ? `${selected.est_minutes}분`
                        : selected.est_minutes % 60 === 0
                          ? `${Math.floor(selected.est_minutes / 60)}시간`
                          : `${Math.floor(selected.est_minutes / 60)}시간 ${selected.est_minutes % 60}분`
                      : "미지정"],
                    ["실제 소요", selected.actual_minutes != null
                      ? selected.actual_minutes < 60
                        ? `${selected.actual_minutes}분`
                        : selected.actual_minutes % 60 === 0
                          ? `${Math.floor(selected.actual_minutes / 60)}시간`
                          : `${Math.floor(selected.actual_minutes / 60)}시간 ${selected.actual_minutes % 60}분`
                      : "미지정"],



                  ].map(([label, value]) => (



                    <div key={label} style={{display:"flex", flexDirection:"column", padding:"8px 0", borderBottom:"1px solid #f0f0f0"}}>
                      <span style={{fontSize:11, color:"#888", marginBottom:3}}>{label}</span>
                      <span style={{fontWeight:500}}>{value}</span>
                    </div>
                  ))}
                </div>

                <div style={{marginTop:16}}>
                  <div style={{fontSize:12, color:"#2b7a2b", fontWeight:700, marginBottom:6}}>업무 내용</div>
                  <div style={{background:"#f6f8fa", borderRadius:8, padding:12, fontSize:13, whiteSpace:"pre-wrap"}}>{selected.content}</div>
                </div>

                {selected.reason_process && (
                  <div style={{marginTop:12}}>
                    <div style={{fontSize:12, color:"#2b7a2b", fontWeight:700, marginBottom:6}}>앞/뒤 프로세스</div>
                    <div style={{background:"#f6f8fa", borderRadius:8, padding:12, fontSize:13}}>{selected.reason_process}</div>
                  </div>
                )}
                {selected.reason_next && (
                  <div style={{marginTop:12}}>
                    <div style={{fontSize:12, color:"#2b7a2b", fontWeight:700, marginBottom:6}}>뒷사람 입장</div>
                    <div style={{background:"#f6f8fa", borderRadius:8, padding:12, fontSize:13}}>{selected.reason_next}</div>
                  </div>
                )}
                {selected.ref_form && (
                  <div style={{marginTop:12}}>
                    <div style={{fontSize:12, color:"#2b7a2b", fontWeight:700, marginBottom:6}}>참고 양식</div>
                    <div style={{background:"#f6f8fa", borderRadius:8, padding:12, fontSize:13}}>{selected.ref_form}</div>
                  </div>
                )}
                {selected.ref_material && (
                  <div style={{marginTop:12}}>
                    <div style={{fontSize:12, color:"#2b7a2b", fontWeight:700, marginBottom:6}}>참고 자료</div>
                    <div style={{background:"#f6f8fa", borderRadius:8, padding:12, fontSize:13}}>{selected.ref_material}</div>
                  </div>
                )}
              </div>

              {/* 오른쪽: 대화 */}
              <div className={styles.modalRight}>
                <div style={{display:"flex", justifyContent:"flex-end", marginBottom:8}}>
                  <button onClick={() => setSelected(null)} style={{
                    border:"1px solid #d0d7de", background:"#f6f8fa",
                    borderRadius:6, padding:"3px 10px", fontSize:12,
                    cursor:"pointer", color:"#555", fontWeight:500, width:"60px"
                  }}>닫기</button>
                </div>
                <TaskQnA taskId={selected.id} currentUserId={me} />
              </div>

            </div>
          </div>
        )} 



      </div>
    </div>
  );
}